// Bridge between the API layer and the sign-in UI, mirroring auth-token.ts:
// SignInGate installs a requester on mount, so thai2english-api.ts can put a
// sign-in screen in front of the user without importing React or Firebase.
//
// The workers refuse a request that carries no valid X-T2E-Client and no
// verified ID token (see ai/docs/deploy-token-gate.md). Signing in is the way
// through, and by the time this module is reached it is the *only* way through:
// refusals a reload can fix are taken by lib/stale-bundle-reload.ts before they
// get here, so what is left is a client whose header a proxy stripped, or one
// that has already reloaded onto the current bundle and is still refused.
// Reloading gives neither of them the header back.

// Type-only, so this stays a module the API layer can import without pulling a
// runtime dependency on the analytics layer.
import type { SignInReason } from 'lib/analytics/events'

export type SignInRequestOptions = {
   /**
    * Whether dismissing leaves the user on a page that can't work.
    *
    * True for an API refusal, which is what this module was built for: every
    * request the page makes is refused, so dismissing has to lead somewhere and
    * the rest of the page's staggered calls have to be silenced.
    *
    * False for a prompt the user asked for by clicking something. The page
    * behind it is perfectly fine — they were reading a word — so dismissing
    * must neither navigate away nor suppress the next deliberate click.
    */
   strandsPage: boolean
   /** Which surface asked, for analytics. */
   source: SignInReason
}

/** Resolves true once the user is signed in, false if they dismissed it. */
export type SignInRequester = (message: string, options: SignInRequestOptions) => Promise<boolean>

// Says why, not what — it renders in a notice bar directly above the modal's
// "Sign in" heading, which already covers the ask. Names the real cause rather
// than blaming load: for a client whose header a proxy stripped, waiting and
// reloading never work, so copy that implies patience sends them in a circle.
export const SignInRequiredMessage =
   "We couldn't confirm this request came from thai2english.com — some networks and browser extensions strip the details we check. Signing in takes just a few seconds, and you'll come straight back to your search."

// The other reason a translation is refused: the free translation capacity has
// run out for the moment and what's left is being held for signed-in users (see
// the worker's login-gate.ts). Unlike the message above, waiting genuinely does
// work here — so it says so, rather than implying signing in is the only way.
export const TranslationBusyMessage =
   'Free translations are busier than usual right now, so signed-in users are going first. Signing in takes a few seconds and puts you straight through — or come back in a few minutes and try again.'

// The user-initiated case, and the only one that isn't an apology: they clicked
// save, so the ask is expected. Says what signing in buys rather than what went
// wrong, and promises the word is still there afterwards.
export const SaveWordSignInMessage =
   'Saving a word keeps it with your account, so it’s waiting for you on every device you use. Signing in takes a few seconds and brings you straight back to this word.'

let requester: SignInRequester | null = null
let pending: Promise<boolean> | null = null
// Set when the user dismisses the screen, cleared on their next navigation.
//
// A page's API calls don't all fire at once — the translation and session calls
// only start once the search has settled — so each refusal would re-open the
// screen the user just closed, with no way out. Suppressing until they navigate
// somewhere new draws the line in the right place: it silences the rest of the
// page they gave up on, but a fresh search is a fresh intent and prompts again.
let suppressed = false

export function setSignInRequester(next: SignInRequester | null): void {
   requester = next
}

/** Called by the sign-in UI when the user dismisses without signing in. */
export function suppressSignInPrompts(): void {
   suppressed = true
}

/** Called by the sign-in UI once the user navigates somewhere new. */
export function resumeSignInPrompts(): void {
   suppressed = false
}

/** The default: a refused API call, which is what every caller here is. */
export const ApiGateRequestOptions: SignInRequestOptions = {
   strandsPage: true,
   source: 'api_gate',
}

/**
 * Asks the user to sign in and resolves once they have (or declined).
 *
 * Concurrent callers share one prompt: a single page load fires the search,
 * the translation and the bulk word lookup at once, and all three are refused
 * together — without this they would each try to open the modal.
 */
export function requestSignIn(
   message: string = SignInRequiredMessage,
   options: SignInRequestOptions = ApiGateRequestOptions
): Promise<boolean> {
   if (!requester) {
      // No UI mounted (tests, SSR-less bootstrap): the caller fails as it did
      // before there was a sign-in screen.
      return Promise.resolve(false)
   }
   // Suppression silences a page's *automatic* retry storm, not a deliberate
   // click. Someone who dismissed an API refusal and then clicked the save
   // heart has asked a second, separate question, and answering it with silence
   // reads as a broken button.
   if (suppressed && options.strandsPage) return Promise.resolve(false)

   // Joining an in-flight prompt rather than opening a second one, even for a
   // user-initiated request: SignInGate holds a single pending request, so
   // asking twice would replace the first one's resolver and leave its caller
   // hanging forever. The first caller's message and options win, which in the
   // only race that can happen (an API refusal already on screen) is the right
   // way round anyway.
   pending ??= requester(message, options).finally(() => {
      pending = null
   })
   return pending
}
